/**
 * The care-plan board, wired to a STAY.
 *
 * adr/0023: an app opens on a subject. This component takes a stay id, reads
 * that stay's fold from the platform, and sends one command per edit. It used
 * to take a session and derive the plan by scanning the transcript backwards
 * for the newest `schedule` block.
 *
 * ONE WIRING, TWO HOMES, exactly as before: full-screen in the agent's own app
 * (apps/care-planner) and in-frame in the main app's Home detail pane. The board
 * owns the data and the commands so the two surfaces cannot drift; each host
 * only gives it height.
 *
 * THE CONVERSATION IS BESIDE THE PLAN, NOT ABOVE IT. The board is complete
 * before anyone types: it comes from the resource, and the composer below is
 * how a nurse instructs the agent about what she is looking at. A conversation
 * is minted on the first message and carries the patient; it is never what the
 * board opens on.
 *
 * THE TWO VOCABULARIES THE BOARD NEEDS are read from where they live, not from
 * the plan:
 *   - disciplines are the org's clinical ROLES (`GET /roles`), which is what
 *     `config/app.yaml care_roles.lead` tells the model to use as
 *     `discipline_id`;
 *   - performers are the people who staff the unit this stay is on, through the
 *     teams that staff that location.
 * Neither is stored in the plan, so neither can go stale inside it, and an
 * intervention keeps the performer's name as it stood when it was assigned.
 */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { orgApi, type Role } from '@frontend/api/endpoints/org'
import { listTeamMembers, listTeamsStaffingLocation } from '@frontend/api/endpoints/teams'
import type { SessionResponse } from '@frontend/api/types_session'
import type { SessionDetail } from '@frontend/hooks/useSessions'
import { ChatInput } from '@frontend/components/chat'
import { BoardConversationDrawer } from '@frontend/components/chat/BoardConversationDrawer'
import { Spinner } from '@frontend/components/ui/spinner'
import { useAuthenticatedQuery } from '@frontend/hooks/useAuthenticatedQuery'
import { useCarePlan, useCarePlanExecution } from '@frontend/hooks/useCarePlan'
import { useDeferredFirstSend } from '@frontend/hooks/useDeferredFirstSend'
import { usePatients, usePatientsConfig } from '@frontend/hooks/usePatients'
import { useStreamingMessage } from '@frontend/hooks/useStreamingChat'
import { QUERY_STALE_TIMES } from '@frontend/lib/query-config'
import { formatPatientLabel } from '@frontend/lib/patientLabel'
import { referenceLabel } from '@frontend/lib/reference'

import { CarePlanBoard, type CarePlanDiscipline, type CarePlanPerformer } from './CarePlanBoard'

/**
 * The org's clinical roles, which are the board's rows.
 *
 * Cached on the org vocabulary's stale window, like every other reader of the
 * org tree: a ward's roles change about as often as the org itself, and far
 * less often than a care plan.
 */
function useDisciplines(): CarePlanDiscipline[] {
  const { data } = useAuthenticatedQuery<Role[]>({
    queryKey: ['carePlan', 'disciplines'],
    queryFn: () => orgApi.listAllRoles(),
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  })
  return useMemo(
    () => (data ?? []).map((role) => ({ id: role.id, name: role.name })),
    [data],
  )
}

/**
 * The people who staff this unit, and therefore the people an act on this stay
 * can be given to.
 *
 * A team is PEOPLE and a location is a place, so "who works here" is a teams
 * question filtered by the unit — the same route the roster reads. The member
 * rows carry `user_ref`, which is the membership's resolved pointer at the
 * person and where the display name comes from; there is no separate name
 * lookup to drift from it.
 */
function usePerformers(locationId: string | null | undefined): CarePlanPerformer[] {
  const { data } = useAuthenticatedQuery<CarePlanPerformer[]>({
    queryKey: ['carePlan', 'performers', locationId ?? 'none'],
    queryFn: async () => {
      const teams = await listTeamsStaffingLocation(locationId as string)
      const rosters = await Promise.all(teams.map((team) => listTeamMembers(team.id)))
      // One person may be on two teams that staff the unit; the picker shows
      // her once.
      const byId = new Map<string, CarePlanPerformer>()
      for (const roster of rosters) {
        for (const member of roster) {
          byId.set(member.user, { id: member.user, name: referenceLabel(member.user_ref) })
        }
      }
      return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
    },
    enabled: !!locationId,
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  })
  return data ?? []
}

export interface CarePlanBoardConnectedProps {
  /** The subject. The board opens on this and reads its fold. */
  stayId: string
  /**
   * The conversation open beside the board, when one has been opened. Absent
   * until the composer's first send mints one, and absent again for a board
   * opened by deep link with no `?c=`.
   */
  session?: SessionDetail
  /** Fires once the composer's first send mints the conversation. */
  onSessionCreated?: (session: SessionResponse) => void
  /**
   * Which agent the conversation belongs to. Supplied by the caller: a care
   * plan is a platform capability and the agent behind it is the caller's
   * business.
   */
  agentSlug: string
}

export function CarePlanBoardConnected({
  stayId,
  session,
  onSessionCreated,
  agentSlug,
}: CarePlanBoardConnectedProps) {
  const { t } = useTranslation()
  const { stay, plan, isLoading, error, apply } = useCarePlan(stayId)
  // What was actually done this week. The planner owns execution for every
  // plan and folds it out of the plan's own change log, so this board reads it
  // and stores none of it.
  const { execution, error: executionError } = useCarePlanExecution(plan)

  const sessionId = session?.session.id ?? ''
  const streaming = useStreamingMessage(sessionId)
  const deferred = useDeferredFirstSend({
    sessionId: session?.session.id,
    agentSlug,
    // The conversation is about this stay's patient, and the board already
    // knows which one that is. Nothing else has to be told.
    patientId: stay?.patient,
    sendMessage: streaming.sendMessage,
    onCreated: onSessionCreated,
  })

  const disciplines = useDisciplines()
  const performers = usePerformers(stay?.location)

  // Patient identity is the REAL patient the stay belongs to, formatted from
  // the deployment's own label format. Never an agent's free text.
  const { data: patients } = usePatients()
  const labelFormat = usePatientsConfig()?.label_format
  const patientLabel = useMemo(() => {
    if (!stay) return null
    const match = (patients ?? []).find((p) => p.token === stay.patient)
    return formatPatientLabel(match?.attributes ?? {}, labelFormat, stay.patient)
  }, [stay, patients, labelFormat])

  return (
    <div className="flex h-full flex-col bg-[var(--color-surface-canvas,#ffffff)]">
      {/* The PLAN read, so the board draws — but what was DONE did not. Without
          this line an activity that was cancelled or missed carries no badge,
          which reads as care still ahead of her rather than care that did not
          happen. It cannot collide with the read-failed state below: the
          execution query is enabled only once a plan has resolved. */}
      {executionError && (
        <div
          role="status"
          className="shrink-0 border-b border-[var(--color-surface-border)] bg-[var(--color-warning-100)] px-4 py-2 text-[13px] text-[var(--color-warning-800)]"
        >
          {t(
            'carePlan.executionUnavailable',
            'What has already been done could not be read, so this week shows the plan only.',
          )}
        </div>
      )}
      {/* Pure sizing container — the board owns its own scroll, so a second
          overflow here would be a redundant nested scroller. */}
      <div className="flex min-h-0 flex-1 flex-col">
        {error ? (
          // The read failed. Not an empty board: an empty week says the patient
          // has nothing planned, which is a statement about her care and this
          // does not know whether it is true.
          <div className="flex flex-1 items-center justify-center p-8 text-center">
            <p className="max-w-sm text-[17px] text-[var(--color-status-danger,#b3261e)]">
              {t('carePlan.readFailed', 'This care plan could not be read.')}
            </p>
          </div>
        ) : isLoading || plan === undefined ? (
          <div className="flex flex-1 items-center justify-center p-8">
            <Spinner className="h-6 w-6 text-[var(--color-surface-mute)]" />
            <span className="sr-only">{t('common.loading', 'Loading...')}</span>
          </div>
        ) : (
          <CarePlanBoard
            plan={plan}
            disciplines={disciplines}
            performers={performers}
            patientLabel={patientLabel}
            execution={execution}
            // A discharged stay's plan is history: it is read, never changed.
            // The server would refuse a command on it, and a board offering
            // controls that cannot work is a board that lies about what it can do.
            isEditable={stay?.status === 'admitted'}
            // No conversation id, deliberately, even when one is open beside
            // the board. adr/0023: "A nurse's board edit cites no conversation;
            // it cites her." The event's author is her identity, which the
            // server takes from the token. Citing whichever conversation
            // happened to be on screen would record that the agent produced a
            // change the nurse made with her own hands, and the stream is what
            // anyone reads afterwards to find out who did what.
            onApply={(intent) => apply(intent)}
          />
        )}
      </div>

      {/* The plan is the surface; the conversation is the instrument. It costs
          one peeking row at rest and opens for a reply that arrives, a turn in
          flight, or an error — see BoardConversationDrawer for the measurements
          behind that. */}
      {session && (
        <BoardConversationDrawer session={session} streaming={streaming} />
      )}

      <div className="shrink-0 border-t border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-4 py-3 pb-safe">
        <ChatInput
          onSend={(content) => void deferred.send(content)}
          isPending={streaming.isStreaming || deferred.isPending}
          placeholder={t('carePlan.chatPlaceholder', 'Instruct the agent…')}
        />
      </div>
    </div>
  )
}
